"use client";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowLeft, ChevronRight, Home } from "lucide-react";
import { useNavItems } from "@/lib/navItems";
import { useNavItemsCorp } from "@/lib/navItemsCorp";

type Crumb = { label: string; href?: string };
type NavLike = {
  label: string;
  linkHref?: string;
  links?: { label: string; href: string }[];
};

function findLabelInNav(path: string, navItems: NavLike[]): string | null {
  for (const item of navItems) {
    if (item.linkHref === path) return item.label;
    const sub = item.links?.find((l) => l.href === path);
    if (sub) return sub.label;
  }
  return null;
}

function humanize(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function BreadcrumbNav({ className = "" }: { className?: string }) {
  const t = useTranslations("breadcrumb");
  const pathname = usePathname();
  const router = useRouter();

  const segments = pathname.split("/").filter(Boolean);
  const locale = segments[0] ?? "es";
  const pathSegments = segments.slice(1);
  const isCorporate = pathSegments[0] === "corporativo";

  const navItems = useNavItems();
  const navItemsCorp = useNavItemsCorp();
  const navSource: NavLike[] = isCorporate ? navItemsCorp : navItems;

  const intermediateSegments = pathSegments.slice(0, -1);
  const crumbs: Crumb[] = intermediateSegments.map((_, idx) => {
    const accumulated = "/" + intermediateSegments.slice(0, idx + 1).join("/");
    const label =
      findLabelInNav(accumulated, navSource) ??
      humanize(intermediateSegments[idx]);
    return { label, href: `/${locale}${accumulated}` };
  });

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
    } else {
      router.push(`/${locale}`);
    }
  };

  return (
    <div
      className={`flex items-center justify-between gap-4 flex-wrap ${className}`}
    >
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-sm text-gray-500 min-w-0 flex-1"
      >
        <Link
          href={`/${locale}`}
          className="inline-flex items-center gap-1 hover:text-[#102542] transition-colors"
        >
          <Home className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t("home")}</span>
        </Link>
        {crumbs.map((crumb) => (
          <span
            key={crumb.href}
            className="inline-flex items-center gap-1.5 min-w-0"
          >
            <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
            {crumb.href ? (
              <Link
                href={crumb.href}
                className="hover:text-[#102542] transition-colors truncate"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="truncate">{crumb.label}</span>
            )}
          </span>
        ))}
      </nav>

      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-[#102542] bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-full border border-gray-200 transition-all shrink-0"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span>{t("back")}</span>
      </button>
    </div>
  );
}
